import { inject, Injectable } from '@angular/core'
import { injectQuery } from '@ngneat/query'
import { HttpClient } from '@angular/common/http'
import { queryKeys } from './query-keys'

const SYNC_POLL_MS = 3000

export const MAX_CALENDAR_SYNC_WAIT_MS = 15 * 60 * 1000

export type CalendarSyncStatus = {
  requestedAt: string | null
  completedAt: string | null
  isCalendarSynchronizationEnabled: boolean
  isConnected: boolean
  hasGivenUp: boolean
  hasInsufficientPermissions: boolean
}

export type CalendarSyncState = 'IDLE' | 'SYNCING' | 'FAILED' | 'FAILED_PERMISSIONS'

export function deriveCalendarSyncState(status: CalendarSyncStatus | null, now: Date = new Date()): CalendarSyncState {
  if (status === null || !status.isCalendarSynchronizationEnabled || !status.isConnected || status.completedAt !== null) {
    return 'IDLE'
  }
  if (status.hasInsufficientPermissions) {
    return 'FAILED_PERMISSIONS'
  }
  if (status.hasGivenUp || hasWaitedTooLong(status.requestedAt, now)) {
    return 'FAILED'
  }
  return 'SYNCING'
}

type CalendarSyncStatusResult = {
  data: CalendarSyncStatus | undefined
  isLoading: boolean
  isError: boolean
}

// The status is only queried for users still waiting on their first synchronization, so an unresolved
// query keeps the loader up instead of flashing an empty call list until the first response arrives.
export function deriveCalendarSyncStateFromResult(result: CalendarSyncStatusResult, now: Date = new Date()): CalendarSyncState {
  if (result.data !== undefined) {
    return deriveCalendarSyncState(result.data, now)
  }
  return result.isLoading || result.isError ? 'SYNCING' : 'IDLE'
}

function hasWaitedTooLong(requestedAt: string | null, now: Date): boolean {
  if (requestedAt === null) {
    return false
  }
  return now.getTime() - new Date(requestedAt).getTime() > MAX_CALENDAR_SYNC_WAIT_MS
}

@Injectable({ providedIn: 'root' })
export class CalendarSyncService {
  private httpClient = inject(HttpClient)
  private query = injectQuery()

  getCalendarSyncStatus() {
    return this.query({
      queryKey: queryKeys.users.calendarSyncStatus,
      queryFn: () => this.httpClient.get<CalendarSyncStatus>('/api/users/me/calendar-sync-status'),
      // A failed poll must keep polling, otherwise a single transient error leaves a syncing user
      // stuck on the loader until they reload the page.
      refetchInterval: (query) => (query.state.status === 'error' || deriveCalendarSyncState(query.state.data ?? null) === 'SYNCING' ? SYNC_POLL_MS : false),
    }).result$
  }
}
